import { FileThumbnail, type SortDirection, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { fileTypeIcon, fileTypeToken, typeMarker } from '@atlas/ui-domain'
import { Badge, Button, Icon } from '@atlas/ui-primitives'
import { X } from 'lucide-react'
import type { AdminConcernRow } from '@/services/file.service'
import type { FileConcernGroup, SortKey } from './group-by-file'

/** Status pill — colour carries the same meaning as the card badges. */
function StatusPill({ status }: { status: AdminConcernRow['status'] }) {
	const variant = status === 'completed' ? 'success' : status === 'failed' ? 'danger' : 'muted'
	return <Badge variant={variant}>{status}</Badge>
}

/** Elapsed time from start to completion — what an operator scans for a slow concern. */
function duration(row: AdminConcernRow): string {
	if (!row.startedAt) return '—'
	const end = row.completedAt ? Date.parse(row.completedAt) : Date.now()
	const ms = end - Date.parse(row.startedAt)
	if (!Number.isFinite(ms) || ms < 0) return '—'
	// Sub-second is the norm for these, so ms up to a second then seconds.
	return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`
}

/** Timestamps read better without the date — the page is a "what just happened" view. */
const clockTime = (iso: string | null): string => (iso ? (iso.split('T')[1]?.replace('Z', '') ?? iso) : '—')

/** One concern, as a data row indented under its file. Compact rows: `py-ds-1` over the kit's default. */
export function ConcernRow({
	row,
	onReprocess,
	onDismiss,
	isReprocessing,
}: {
	row: AdminConcernRow
	onReprocess: (row: AdminConcernRow) => void
	onDismiss: (resultId: string) => void
	isReprocessing: boolean
}) {
	return (
		<TableRow>
			<TableCell className="py-ds-1 pl-ds-8">
				<code className="font-ds-mono text-ds-xs text-ds-text">{row.concernId}</code>
			</TableCell>
			<TableCell className="py-ds-1">
				<div className="flex items-center gap-ds-2">
					<StatusPill status={row.status} />
					{row.stale && <Badge variant="warning">stale</Badge>}
				</div>
			</TableCell>
			<TableCell className="whitespace-nowrap py-ds-1 font-ds-mono text-ds-xs text-ds-text-muted">
				{clockTime(row.startedAt)}
			</TableCell>
			<TableCell className="whitespace-nowrap py-ds-1 font-ds-mono text-ds-xs text-ds-text-muted">
				{duration(row)}
			</TableCell>
			<TableCell className="py-ds-1 text-ds-xs text-ds-danger">{row.errorMessage ?? ''}</TableCell>
			<TableCell className="py-ds-1">
				<div className="flex items-center justify-end gap-ds-1">
					<Button variant="muted" isDisabled={isReprocessing} onPress={() => onReprocess(row)}>
						Reprocess
					</Button>
					{/* Hides the row locally only — these are diagnostics records, so
					    nothing is deleted server-side. Refetching brings it back. */}
					<Button variant="muted" onPress={() => onDismiss(row.resultId)} aria-label={`Hide ${row.concernId}`}>
						<Icon size="sm">
							<X />
						</Icon>
					</Button>
				</div>
			</TableCell>
		</TableRow>
	)
}

/** A file's header row: preview, name, and the state rolled up from its concerns. */
export function FileHeaderRow({ group }: { group: FileConcernGroup }) {
	// Full-opacity separator and elevation kept over the kit row's defaults —
	// including on hover, so the elevated row never visibly de-elevates.
	return (
		<TableRow className="border-ds-border bg-ds-surface-elevated hover:bg-ds-surface-elevated">
			<TableCell colSpan={5}>
				<div className="flex items-center gap-ds-2">
					{/* `md`, not the list default: this row has the height for a marker,
					    and naming the format is most useful here — a diagnostics view is
					    where an operator asks why a file has no preview. */}
					<FileThumbnail
						name={group.fileName ?? group.fileId}
						thumbnailUrl={group.thumbnailUrl}
						size="md"
						icon={fileTypeIcon(fileTypeToken(group.contentType, group.fileName ?? undefined))}
						marker={typeMarker(group.contentType ?? '')}
					/>
					<div className="flex min-w-0 flex-col">
						<span className="truncate text-ds-sm text-ds-text">{group.fileName ?? '—'}</span>
						<code className="truncate font-ds-mono text-ds-xs text-ds-text-muted">{group.fileId}</code>
					</div>
					{/*
					 * A concern about a deleted file is exactly what an operator is
					 * investigating, so name it and say it is gone rather than hiding it.
					 */}
					{group.fileDeleted && (
						<Badge variant="muted" className="whitespace-nowrap">
							file deleted
						</Badge>
					)}
					{/* Rolled up from the concerns below: which FILE is stuck is the
					    question this page exists to answer. */}
					{group.hasFailed && <Badge variant="danger">failed</Badge>}
					{group.hasStale && <Badge variant="warning">stale</Badge>}
				</div>
			</TableCell>
			<TableCell className="whitespace-nowrap text-right text-ds-xs text-ds-text-muted">
				{group.concerns.length} {group.concerns.length === 1 ? 'concern' : 'concerns'}
			</TableCell>
		</TableRow>
	)
}

const COLUMNS: { key: SortKey | null; label: string; className?: string }[] = [
	{ key: 'file', label: 'File / concern' },
	{ key: 'status', label: 'Status' },
	{ key: null, label: 'Started' },
	{ key: null, label: 'Duration' },
	{ key: null, label: 'Error' },
	{ key: 'concerns', label: 'Count', className: 'text-right' },
]

/** Column headers, with the sortable ones as buttons carrying the current direction. */
export function ConcernTableHeader({
	sortKey,
	sortDirection,
	onSort,
}: {
	sortKey: SortKey
	sortDirection: SortDirection
	onSort: (key: SortKey) => void
}) {
	return (
		<TableHeader>
			<TableRow>
				{COLUMNS.map((column) => (
					<TableHead
						key={column.label}
						className={column.className}
						sortDirection={column.key === sortKey ? sortDirection : null}
						onSort={column.key ? () => onSort(column.key as SortKey) : undefined}
					>
						{column.label}
					</TableHead>
				))}
			</TableRow>
		</TableHeader>
	)
}
